import styled from "@emotion/styled"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from "@mui/material"
import Image from "next/image"
import { Fragment } from "react"
import { Mobile, useIsDesktop } from "ui"
import { sharedColors } from "ui/theme/colors"
import { Link, replaceRichTextLinks, type ContactUsInnerItem } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"

const LocationAccordion = styled(Accordion)`
  background-color: ${sharedColors.white};
  border-radius: 16px !important;
  box-shadow: 0px 0px 16px 0px rgba(0, 0, 0, 0.08);
  margin: 0 !important;
  border: 1px solid ${sharedColors.borderGray} !important;

  &::before {
    display: none;
  }
  .MuiAccordionSummary-content p {
    font-weight: unset !important;
  }
  .MuiAccordionSummary-root {
    background: rgba(27, 189, 119, 0.08);

    padding: 16px !important;
    ${({ theme }) => theme.breakpoints.up("md")} {
      padding: 24px !important;
    }
    &.Mui-expanded {
      cursor: text;
      user-select: text;
      box-shadow: inset 0 -1px 0 0 ${sharedColors.borderGray};
    }
    &:focus-visible {
      border: 1px dashed white;
      outline-offset: 2px;
      outline: 2px solid black;
    }
  }

  .MuiAccordionSummary-content {
    display: flex;
    align-items: center;
    margin: 0 !important;
    color: ${sharedColors.black};
  }

  .MuiAccordionDetails-root {
    padding: 16px 12px !important;
    display: flex !important;
    flex-direction: column !important;
    gap: 16px;
    ${({ theme }) => theme.breakpoints.up("md")} {
      padding: 24px !important;
      gap: 24px;
    }
  }

  .Mui-expanded {
    margin: 0 !important;
  }
  a {
    color: #051711;
  }
`

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }
`

const Section = styled("div", {
  shouldForwardProp: (prop) => prop !== "hasBorder" && prop !== "empty",
})<{ hasBorder?: boolean; empty?: boolean }>`
  display: ${({ empty }) => (empty ? "none" : "flex")};
  flex-direction: column;
  gap: 16px;
  min-width: 0;

  ${({ theme }) => theme.breakpoints.up("md")} {
    display: ${({ empty }) => (empty ? "none" : "flex")};
    border-block-end: none;
    padding-block-end: 0;
    border-inline-start: ${({ hasBorder }) =>
      hasBorder ? `1px solid ${sharedColors.borderGray}` : "none"};
    padding-inline-start: ${({ hasBorder }) => (hasBorder ? "24px" : "0")};
  }
`

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`

const HoursGrid = styled.div`
  display: grid;
  grid-template-columns: fit-content(55%) auto;
  column-gap: 32px;
  row-gap: 16px;
  align-items: start;
`

const StyledExpandIcon = styled(Image, {
  shouldForwardProp: (prop) => prop !== "isExpanded",
})<{ isExpanded: boolean }>`
  transition: transform 0.3s ease;
  transform: ${({ isExpanded }) =>
    isExpanded ? "rotate(180deg)" : "rotate(0deg)"};
`

const StyledSubTitle = styled(Typography)`
  font-weight: 400 !important;
`

const CappedTypography = styled(Typography)`
  text-box-trim: trim-both;
  text-box-edge: cap alphabetic;
`

const DayColumn = styled(CappedTypography)`
  min-width: 110px;
  flex-shrink: 0;
`

const DetailsGrid = styled("div", {
  shouldForwardProp: (prop) => prop !== "columns",
})<{ columns: number }>`
  display: flex;
  flex-direction: column;
  gap: 16px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    display: grid;
    grid-template-columns: ${({ columns }) =>
      columns <= 1 ? "auto" : `auto repeat(${columns - 1}, minmax(0, 1fr))`};
    justify-content: start;
    align-items: stretch;
    gap: 24px;
  }
`

interface LocationCardProps {
  item: ContactUsInnerItem
  isExpanded: boolean
  onToggle: () => void
}

function parseRichTextTitle(richText: string | undefined): {
  title: string
  subtitle: string
} {
  if (!richText) return { title: "", subtitle: "" }

  const strongMatch = richText.match(/<strong>(.*?)<\/strong>/)
  const title = strongMatch ? strongMatch[1] : ""

  const textAfterStrong = richText
    .replace(/<p>/g, "")
    .replace(/<\/p>/g, "")
    .replace(/<strong>.*?<\/strong>/g, "")
    .replace(/^\s*-\s*/, "")
    .trim()

  return { title, subtitle: textAfterStrong }
}

export default function LocationCard({
  item,
  isExpanded,
  onToggle,
}: LocationCardProps) {
  const isDesktop = useIsDesktop()
  const { title, subtitle } = parseRichTextTitle(item.itemTitle)

  const hasOpeningHours = Boolean(
    item.openingHoursTitle || item.openingHoursContent.length > 0,
  )
  const hasBreakHours = Boolean(
    item.breakHoursTitle || item.breakHoursContent.length > 0,
  )
  const hasAddress = Boolean(
    item.addressTitle ||
      item.address ||
      (item.addressLink && item.addressLink.length > 0),
  )
  const hasFax = Boolean(item.faxTitle || item.faxNumber)
  const visibleSectionCount =
    [hasOpeningHours, hasBreakHours, hasAddress, hasFax].filter(Boolean)
      .length || 1
  // Desktop: always expanded, no toggle. Mobile: controlled by state
  const accordionExpanded = isDesktop ? true : isExpanded
  const handleChange = isDesktop ? undefined : onToggle

  return (
    <LocationAccordion expanded={accordionExpanded} onChange={handleChange}>
      <AccordionSummary
        expandIcon={
          <Mobile>
            <StyledExpandIcon
              alt=""
              width={24}
              height={24}
              src="/icons/arrow-down.svg"
              isExpanded={isExpanded}
            />
          </Mobile>
        }
      >
        <HeaderContent>
          {title && (
            <Typography variant="body1" fontWeight={700}>
              {title}
            </Typography>
          )}
          {subtitle && (isDesktop || isExpanded || !title) && (
            <Typography
              variant="body2"
              component="span"
              sx={{ fontWeight: 400 }}
            >
              {subtitle}
            </Typography>
          )}
        </HeaderContent>
      </AccordionSummary>

      <AccordionDetails>
        {item.importantToKnowTitle && (
          <div>
            <Typography variant="body1" component={"h4"} fontWeight={700}>
              {item.importantToKnowTitle}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                "& p": {
                  margin: 0,
                },
                marginTop: "8px",
              }}
              dangerouslySetInnerHTML={{
                __html: replaceRichTextLinks(item.importantToKnowContent || ""),
              }}
            />
          </div>
        )}

        <DetailsGrid columns={visibleSectionCount}>
          <Section hasBorder={false} empty={!hasOpeningHours}>
            {hasOpeningHours && (
              <>
                <SectionHeader>
                  {item.openingHoursIcon && (
                    <Image
                      alt=""
                      width={20}
                      height={20}
                      src={getUmbracoMediaUrl(item.openingHoursIcon) || ""}
                    />
                  )}
                  <CappedTypography variant="body2" fontWeight={700}>
                    {item.openingHoursTitle}
                  </CappedTypography>
                </SectionHeader>
                <HoursGrid>
                  {item.openingHoursContent.map((el, i) => (
                    <Fragment key={i}>
                      <DayColumn variant="body2">{el.day}</DayColumn>
                      <CappedTypography variant="body2">
                        {el.hour}
                      </CappedTypography>
                    </Fragment>
                  ))}
                </HoursGrid>
              </>
            )}
          </Section>

          <Section hasBorder empty={!hasBreakHours}>
            {hasBreakHours && (
              <>
                <SectionHeader>
                  {item.breakHoursIcon && (
                    <Image
                      alt=""
                      width={20}
                      height={20}
                      src={getUmbracoMediaUrl(item.breakHoursIcon) || ""}
                    />
                  )}
                  <CappedTypography variant="body2" fontWeight={700}>
                    {item.breakHoursTitle}
                  </CappedTypography>
                </SectionHeader>
                {item.breakHoursContent.map((el, i) => (
                  <CappedTypography key={i} variant="body2">
                    {el.hour}
                  </CappedTypography>
                ))}
              </>
            )}
          </Section>

          <Section hasBorder empty={!hasAddress}>
            {hasAddress && (
              <>
                <SectionHeader>
                  {item.addressIcon && (
                    <Image
                      alt=""
                      width={20}
                      height={20}
                      src={getUmbracoMediaUrl(item.addressIcon) || ""}
                    />
                  )}
                  {item.addressTitle && (
                    <CappedTypography variant="body2" fontWeight={700}>
                      {item.addressTitle}
                    </CappedTypography>
                  )}
                </SectionHeader>
                {item.address && (
                  <CappedTypography
                    variant="body2"
                    sx={{
                      textDecoration: "underline",
                      whiteSpace: "pre-wrap",
                    }}
                    dangerouslySetInnerHTML={{
                      __html: replaceRichTextLinks(item.address),
                    }}
                  />
                )}
                {item.addressLink && item.addressLink.length > 0 && (
                  <Link
                    href={item.addressLink[0].url || "#"}
                    target={item.addressLink[0].target || undefined}
                  >
                    <CappedTypography
                      variant="body2"
                      sx={{
                        textDecoration: "underline",
                        whiteSpace: "pre-wrap",
                      }}
                    >
                      {item.addressLink[0].name}
                    </CappedTypography>
                  </Link>
                )}
              </>
            )}
          </Section>

          <Section hasBorder empty={!hasFax}>
            {hasFax && (
              <>
                <SectionHeader>
                  {item.faxIcon && (
                    <Image
                      alt=""
                      width={20}
                      height={20}
                      src={getUmbracoMediaUrl(item.faxIcon) || ""}
                    />
                  )}
                  <CappedTypography variant="body2" fontWeight={700}>
                    {item.faxTitle}
                  </CappedTypography>
                </SectionHeader>
                {item.faxNumber && (
                  <CappedTypography variant="body2">
                    {item.faxNumber}
                  </CappedTypography>
                )}
              </>
            )}
          </Section>
        </DetailsGrid>
      </AccordionDetails>
    </LocationAccordion>
  )
}
